/**
 * Medical Necessity -- LCD / NCD coverage check inside the workflow shell.
 *
 * Route: /MedicalNecessity
 *
 * 5-step shell:
 *   1. Pick a sample (CPT + ICD combination), or type codes in.
 *   2. Optionally drop chart notes / op reports for downstream reference.
 *   3. Run the coverage check (checkMedicalNecessity).
 *   4. Verdict table + code descriptions + summary stats.
 *   5. LCD/Article criteria walkthrough (checkboxes per governing article).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  FileDropZone,
  ResultsPanel,
  Icon,
  ExampleModal,
  getWorkflowModule,
} from '../../components/workflow';
import { checkMedicalNecessity, checkMedicalNecessityV5, fetchMedicalNecessityCriteria } from '../../services/suiteApi';
import { MEDICAL_NECESSITY_EXAMPLE } from '../../fixtures/exampleInputs';
import { getSampleById } from '../../fixtures/testCases';
import './suite.css';

const VERDICT_META = {
  supported:       { label: 'Supported',       cls: 'suite-badge--ok' },
  not_supported:   { label: 'Not supported',   cls: 'suite-badge--bad' },
  needs_review:    { label: 'Needs review',    cls: 'suite-badge--warn' },
  no_policy_found: { label: 'No policy found', cls: 'suite-badge--muted' },
};

function summariseResult(codes, icd, result, sample, files) {
  if (!result) return '';
  const verdicts = result.verdicts || [];
  const lines = verdicts.map((v) => {
    const m = VERDICT_META[v.medicareVerdict || v.verdict] || VERDICT_META.no_policy_found;
    const pv = VERDICT_META[v.payerVerdict || 'no_policy_found'] || VERDICT_META.no_policy_found;
    const desc = (result.codeDescriptions || {})[v.cptCode] || '';
    return `${v.cptCode}${desc ? ` (${desc})` : ''}: Medicare=${m.label}; ${result.payerCode || 'Commercial'}=${pv.label}; supporting Dx=[${(v.supportingDx || []).join(', ')}]; blocking Dx=[${(v.blockingDx || []).join(', ')}]`;
  }).join('\n');
  const fileNote = files.length ? `\nAttached chart notes: ${files.map((f) => f.name).join(', ')}` : '';
  return [
    `MEDICAL NECESSITY — ${sample ? `sample: ${sample.title}` : 'check'}`,
    `CPT: ${codes}`,
    `ICD: ${icd}`,
    result.payerCode ? `Payer: ${result.payerCode}` : '',
    fileNote,
    '',
    lines || 'No verdicts returned.',
  ].filter(Boolean).join('\n');
}

function CriteriaWalkthrough({ criteria, loading }) {
  const [checked, setChecked] = useState({});

  if (loading) {
    return (
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">5</span>
            LCD / Article criteria walkthrough
          </h2>
        </header>
        <p style={{ fontSize: 13, color: 'var(--dm-muted)' }}>Loading criteria…</p>
      </section>
    );
  }

  if (!criteria || !criteria.articles || criteria.articles.length === 0) return null;

  const toggle = (artId, critId) => {
    setChecked((prev) => ({ ...prev, [`${artId}/${critId}`]: !prev[`${artId}/${critId}`] }));
  };

  const totalItems = criteria.articles.reduce((n, a) => n + (a.criteria || []).length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;

  return (
    <section className="wf-step-card">
      <header className="wf-step-card__head">
        <h2 className="wf-step-card__title">
          <span className="wf-step-card__num">5</span>
          LCD / Article criteria walkthrough
        </h2>
        {totalItems > 0 && (
          <span style={{ fontSize: 12, color: 'var(--dm-muted)', fontWeight: 600 }}>
            {checkedCount} / {totalItems} met
          </span>
        )}
      </header>
      <p style={{ fontSize: 13, color: 'var(--dm-muted)', marginBottom: 14 }}>
        Walk through the coverage criteria for the governing LCD/Article(s). Check off items your documentation supports.
      </p>
      {criteria.articles.map((art) => (
        <div key={art.articleId} className="mn-criteria-article">
          <h4 className="mn-criteria-article__title">
            <span className="suite-badge suite-badge--info">{art.articleId}</span>
            {art.title}
          </h4>
          {(art.criteria || []).length === 0 ? (
            art.narrative ? (
              <div style={{ fontSize: 12, color: '#4a5d7a', marginLeft: 12, lineHeight: 1.55, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', padding: '8px 12px', background: '#f5f7fb', borderRadius: 6 }}>
                {art.narrative}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--dm-muted)', marginLeft: 12 }}>No structured criteria parsed for this article.</p>
            )
          ) : (
            <ul className="mn-criteria-list">
              {art.criteria.map((c, idx) => {
                const key = `${art.articleId}/${c.id || idx}`;
                return (
                  <li key={key} className={`mn-criteria-item ${checked[key] ? 'mn-criteria-item--met' : ''}`}>
                    <label className="mn-criteria-item__label">
                      <input
                        type="checkbox"
                        checked={!!checked[key]}
                        onChange={() => toggle(art.articleId, c.id || idx)}
                      />
                      <span>{c.text || c.description || JSON.stringify(c)}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}
    </section>
  );
}

export default function MedicalNecessityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('medical-necessity');

  const [files, setFiles] = useState([]);
  const [codes, setCodes] = useState('');
  const [icd, setIcd] = useState('');
  const [payerId, setPayerId] = useState('');
  const [clinicalText, setClinicalText] = useState('');
  const [useV5, setUseV5] = useState(true);
  const [showExample, setShowExample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busySampleId, setBusySampleId] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [v5Result, setV5Result] = useState(null);
  const [sample, setSample] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  const printRef = useRef(null);

  const sampleId = searchParams.get('sample');

  const run = async ({ codes: c, icd: i, payerId: p, clinicalText: ct } = {}) => {
    setBusy(true); setError(''); setResult(null); setV5Result(null); setCriteria(null);
    const codeVal = c ?? codes;
    const icdVal = i ?? icd;
    const textVal = ct ?? clinicalText;
    try {
      if (useV5 && textVal.trim()) {
        const codeList = codeVal.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const icdList = icdVal.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const data = await checkMedicalNecessityV5({
          clinicalText: textVal,
          codes: codeList,
          icd: icdList,
          payer: (p ?? payerId) || undefined,
        });
        if (!data?.success) throw new Error(data?.message || 'Check failed');
        setV5Result(data.data);
      } else {
        const codeList = codeVal.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const icdList = icdVal.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        const data = await checkMedicalNecessity({ codes: codeList, icd: icdList, payerId: (p ?? payerId) || undefined });
        if (!data?.success) throw new Error(data?.message || 'Check failed');
        setResult(data.data);
        loadCriteria(codeList, icdList);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const loadCriteria = async (codeList, icdList) => {
    setCriteriaLoading(true);
    try {
      const resp = await fetchMedicalNecessityCriteria({ codes: codeList, icd: icdList });
      if (resp?.success) setCriteria(resp.data);
    } catch {
      // Criteria is optional; don't block results on failure
    } finally {
      setCriteriaLoading(false);
    }
  };

  const onPickSample = async (s) => {
    setBusySampleId(s.id);
    setSample(s);
    setCodes(s.payload.codes || '');
    setIcd(s.payload.icd || '');
    if (s.payload.clinicalText) setClinicalText(s.payload.clinicalText);
    await run({ codes: s.payload.codes, icd: s.payload.icd, clinicalText: s.payload.clinicalText });
    setBusySampleId(null);
  };

  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'medical-necessity') onPickSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  const onRun = () => {
    setSample(null);
    if (sampleId) setSearchParams({});
    run();
  };

  const onReset = () => {
    setResult(null);
    setV5Result(null);
    setCriteria(null);
    setError('');
    setCodes('');
    setIcd('');
    setPayerId('');
    setClinicalText('');
    setFiles([]);
    setSample(null);
    if (sampleId) setSearchParams({});
  };

  const currentStep = (result || v5Result) ? 'results' : (codes.trim() || clinicalText.trim() ? 'run' : 'choose');
  const chatContext = useMemo(() => summariseResult(codes, icd, result, sample, files), [codes, icd, result, sample, files]);

  const verdicts = result?.verdicts || [];
  const supportedCount = verdicts.filter((v) => v.medicareVerdict === 'supported').length;
  const notSupportedCount = verdicts.filter((v) => v.medicareVerdict === 'not_supported').length;
  const reviewCount = verdicts.filter((v) => v.medicareVerdict === 'needs_review').length;
  const noPolicyCount = verdicts.filter((v) => v.medicareVerdict === 'no_policy_found').length;
  const codeDescs = result?.codeDescriptions || {};

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      caseContext={chatContext}
    >
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">1</span>
            Clinical notes + codes
          </h2>
        </header>
        {useV5 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Clinical Documentation
              </label>
              <button type="button" className="example-link" onClick={() => setShowExample(true)}>View Example</button>
            </div>
            <textarea
              value={clinicalText}
              onChange={(e) => setClinicalText(e.target.value)}
              rows={12}
              placeholder="Paste clinical note, chart documentation, HPI, exam findings, assessment & plan..."
              style={{ marginTop: 6, width: '100%', padding: 10, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 13, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box' }}
            />
          </div>
        )}
        {!useV5 && (
          <>
            <p className="wf-step-card__hint">{moduleDef.uploadHint}</p>
            <FileDropZone
              files={files}
              onFiles={setFiles}
              accept={moduleDef.accepts}
              multiple
              hint="Chart pages and op reports (PDF/DOCX/TXT) attach to the case for the chat."
            />
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            CPT / HCPCS
            <input value={codes} onChange={(e) => setCodes(e.target.value)} style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            ICD-10 supporting Dx
            <input value={icd} onChange={(e) => setIcd(e.target.value)} style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Payer (optional)
            <input value={payerId} onChange={(e) => setPayerId(e.target.value)} placeholder="Medicare" style={{ padding: 9, border: '1px solid var(--dm-border)', borderRadius: 8, fontSize: 14, color: 'var(--dm-ink)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }} />
          </label>
        </div>
        <div className="wf-actions">
          <button type="button" className="wf-btn" disabled={busy || (useV5 && !clinicalText.trim())} onClick={onRun}>
            <Icon.play /> {busy ? 'Analyzing…' : (useV5 ? 'Run Coverage Review' : moduleDef.runLabel)}
          </button>
          <button type="button" className="wf-btn wf-btn--ghost" onClick={onReset}>
            Reset
          </button>
        </div>
        {error && <div className="wf-error">{error}</div>}
      </section>

      {/* V5 Rich Output */}
      {v5Result && <V5CoverageReport report={v5Result} printRef={printRef} />}

      {/* V4 Legacy Output */}
      {result && (
        <ResultsPanel
          title="LCD / NCD verdict per code"
          data={result}
          downloadName={`medical-necessity_${sample?.id || 'check'}`}
          textReport={chatContext}
          printableRef={printRef}
        >
          <div ref={!v5Result ? printRef : undefined}>
            <dl className="wf-kv">
              <div><dt>Codes checked</dt><dd>{verdicts.length}</dd></div>
              <div><dt>Supported</dt><dd style={{ color: '#16a085' }}>{supportedCount}</dd></div>
              <div><dt>Not supported</dt><dd style={{ color: '#c0392b' }}>{notSupportedCount}</dd></div>
              <div><dt>Needs review</dt><dd style={{ color: '#b07a16' }}>{reviewCount}</dd></div>
              {noPolicyCount > 0 && <div><dt>No policy</dt><dd>{noPolicyCount}</dd></div>}
            </dl>
            <table className="suite-table" style={{ marginTop: 14 }}>
              <thead>
                <tr>
                  <th>CPT</th>
                  <th>Medicare</th>
                  <th>{result.payerCode || 'Commercial'}</th>
                  <th>Supporting Dx</th>
                  <th>Blocking Dx</th>
                  <th>Policy</th>
                </tr>
              </thead>
              <tbody>
                {verdicts.map((v) => {
                  const m = VERDICT_META[v.medicareVerdict || v.verdict] || VERDICT_META.no_policy_found;
                  const pv = VERDICT_META[v.payerVerdict || 'no_policy_found'] || VERDICT_META.no_policy_found;
                  const desc = codeDescs[v.cptCode];
                  return (
                    <tr key={v.cptCode}>
                      <td>
                        <b>{v.cptCode}</b>
                        {desc && <div style={{ fontSize: 11, color: 'var(--dm-muted)', marginTop: 2 }}>{desc}</div>}
                      </td>
                      <td><span className={`suite-badge ${m.cls}`}>{m.label}</span></td>
                      <td><span className={`suite-badge ${pv.cls}`}>{pv.label}</span></td>
                      <td>{(v.supportingDx || []).map((d) => (
                        <span key={d} style={{ display: 'inline-block', marginRight: 6 }}>
                          {d}{codeDescs[d] && <span style={{ fontSize: 10, color: 'var(--dm-muted)' }}> ({codeDescs[d]})</span>}
                        </span>
                      ))}{(v.supportingDx || []).length === 0 && '—'}</td>
                      <td>{(v.blockingDx || []).map((d) => (
                        <span key={d} style={{ display: 'inline-block', marginRight: 6 }}>
                          {d}{codeDescs[d] && <span style={{ fontSize: 10, color: 'var(--dm-muted)' }}> ({codeDescs[d]})</span>}
                        </span>
                      ))}{(v.blockingDx || []).length === 0 && '—'}</td>
                      <td>
                        {[...(v.payerNarratives || []), ...(v.narratives || [])].slice(0, 2).map((n, idx) => (
                          <div key={idx} style={{ marginBottom: 6 }}>
                            <b>{n.title}</b>
                            {n.description && (
                              <div style={{ color: 'var(--dm-muted)', fontSize: 12 }}>
                                {String(n.description)}
                              </div>
                            )}
                          </div>
                        ))}
                        {((v.narratives || []).length + (v.payerNarratives || []).length) === 0 && '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ResultsPanel>
      )}

      <CriteriaWalkthrough criteria={criteria} loading={criteriaLoading} />
      <ExampleModal open={showExample} onClose={() => setShowExample(false)} title="Example: Medical Necessity Input" content={MEDICAL_NECESSITY_EXAMPLE} />
    </WorkflowLayout>
  );
}

/* ---- V5 Rich Coverage Report Component ---- */

const STATUS_META = {
  BILL_READY:          { label: 'Bill Ready',          cls: 'suite-badge--ok' },
  NOT_BILL_READY:      { label: 'Not Bill-Ready',     cls: 'suite-badge--bad' },
  NEEDS_REVIEW:        { label: 'Needs Review',       cls: 'suite-badge--warn' },
  NEEDS_CODING_UPDATE: { label: 'Needs Coding Update', cls: 'suite-badge--warn' },
  NOT_SUPPORTED:       { label: 'Not Supported',      cls: 'suite-badge--bad' },
  HOLD:                { label: 'Hold',               cls: 'suite-badge--warn' },
};

const CROSSWALK_META = {
  MET:                 { label: 'Met',                cls: 'suite-badge--ok' },
  NOT_MET:             { label: 'Not Met',            cls: 'suite-badge--bad' },
  NEEDS_CODING_REVIEW: { label: 'Needs Coding Review', cls: 'suite-badge--warn' },
  UNKNOWN:             { label: 'Unknown',            cls: 'suite-badge--muted' },
};

function V5CoverageReport({ report, printRef }) {
  const sm = STATUS_META[report.overallStatus] || STATUS_META.NEEDS_REVIEW;

  const renderWithCitations = (text) => {
    if (!text) return '—';
    const parts = text.split(/(\(Source:[^)]+\))/g);
    return parts.map((part, i) =>
      part.startsWith('(Source:')
        ? <span key={i} style={{ fontSize: 11, color: '#7f8c8d', fontStyle: 'italic' }}>{part}</span>
        : part
    );
  };

  return (
    <section className="wf-step-card" ref={printRef}>
      {/* Executive Summary */}
      <header className="wf-step-card__head">
        <h2 className="wf-step-card__title">
          <span className="wf-step-card__num">3</span>
          Coverage Review Result
        </h2>
        <span className={`suite-badge ${sm.cls}`} style={{ fontSize: 13 }}>{sm.label}</span>
      </header>

      <div style={{ background: report.overallStatus === 'BILL_READY' ? '#e8f8f5' : '#fef9e7', borderRadius: 8, padding: '14px 18px', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{report.recommendedAction}</div>
        <div style={{ fontSize: 13, color: '#34495e' }}>{renderWithCitations(report.reason)}</div>
        {report.confidence > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--dm-muted)' }}>
            Confidence: <b>{report.confidence}%</b> — {report.confidenceRationale}
          </div>
        )}
      </div>

      {/* Line Results */}
      {(report.lineResults || []).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Line-Level Coverage</h3>
          <table className="suite-table">
            <thead>
              <tr><th>CPT</th><th>Service</th><th>Status</th><th>Clinical Support</th><th>Dx Problem</th><th>Risk</th></tr>
            </thead>
            <tbody>
              {report.lineResults.map((lr, i) => {
                const lm = STATUS_META[lr.status] || STATUS_META.NEEDS_REVIEW;
                return (
                  <tr key={i}>
                    <td><b>{lr.cptCode}</b></td>
                    <td style={{ fontSize: 12 }}>{lr.service}</td>
                    <td><span className={`suite-badge ${lm.cls}`}>{lm.label}</span></td>
                    <td>{lr.clinicalSupportFound ? <span style={{ color: '#16a085' }}>Yes</span> : <span style={{ color: '#c0392b' }}>No</span>}</td>
                    <td style={{ fontSize: 12 }}>{lr.diagnosisSupportProblem || '—'}</td>
                    <td style={{ fontSize: 12 }}>{lr.primaryRisk || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Evidence Extracted */}
      {(report.evidenceExtracted || []).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Evidence Extracted from Clinical Note</h3>
          {report.evidenceExtracted.map((ev, i) => (
            <div key={i} style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', marginBottom: 10, border: '1px solid var(--dm-border)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>CPT {ev.cptCode}: {ev.service}</div>
              {(ev.evidence || []).map((cat, ci) => (
                <div key={ci} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dm-muted)', textTransform: 'uppercase' }}>{cat.category}</div>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12, lineHeight: 1.5 }}>
                    {(cat.items || []).map((item, ii) => <li key={ii}>{item}</li>)}
                  </ul>
                </div>
              ))}
              {ev.interpretation && (
                <div style={{ marginTop: 8, fontSize: 12, fontStyle: 'italic', color: '#4a5d7a' }}>{ev.interpretation}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Coverage Crosswalk */}
      {(report.coverageLookup || []).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>LCD / NCD Coverage Crosswalk</h3>
          {report.coverageLookup.map((cl, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                CPT {cl.cptCode}
                {cl.articleId && <span style={{ fontSize: 11, marginLeft: 8, color: 'var(--dm-muted)' }}>[{cl.articleId}] {cl.articleTitle || ''}</span>}
              </div>
              {cl.policyNotFound && (
                <div style={{ fontSize: 12, color: '#b07a16', marginBottom: 6 }}>{cl.policyNotFoundNote || 'No LCD/NCD policy found in knowledge base.'}</div>
              )}
              {(cl.crosswalk || []).length > 0 && (
                <table className="suite-table" style={{ fontSize: 12 }}>
                  <thead><tr><th>Requirement</th><th>Status</th><th>Evidence</th><th>Explanation</th></tr></thead>
                  <tbody>
                    {cl.crosswalk.map((cw, ci) => {
                      const cwm = CROSSWALK_META[cw.status] || CROSSWALK_META.UNKNOWN;
                      return (
                        <tr key={ci}>
                          <td>{cw.requirement}</td>
                          <td><span className={`suite-badge ${cwm.cls}`}>{cwm.label}</span></td>
                          <td>{cw.evidence || '—'}</td>
                          <td>{renderWithCitations(cw.explanation)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Coding Review */}
      {report.codingReview && report.codingReview.assessment && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Coding Support Review</h3>
          <p style={{ fontSize: 13, marginBottom: 8 }}>{renderWithCitations(report.codingReview.assessment)}</p>
          {(report.codingReview.perCode || []).map((pc, i) => (
            <div key={i} style={{ background: '#fef9e7', borderRadius: 6, padding: '8px 12px', marginBottom: 6, fontSize: 12 }}>
              <b>CPT {pc.cptCode}</b> (current Dx: {pc.currentDx}): {pc.problem}
              {pc.recommendation && <div style={{ marginTop: 4, color: '#4a5d7a' }}>{pc.recommendation}</div>}
            </div>
          ))}
          {report.codingReview.important && (
            <p style={{ fontSize: 11, color: 'var(--dm-muted)', fontStyle: 'italic', marginTop: 8 }}>{report.codingReview.important}</p>
          )}
        </div>
      )}

      {/* Pre-Bill Decision */}
      {report.preBillDecision && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Pre-Bill Decision</h3>
          <div style={{ background: '#f0f4f8', borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              <span className={`suite-badge ${(STATUS_META[report.preBillDecision.finalDecision] || STATUS_META.NEEDS_REVIEW).cls}`}>
                {(STATUS_META[report.preBillDecision.finalDecision] || STATUS_META.NEEDS_REVIEW).label}
              </span>
            </div>
            <p style={{ fontSize: 13, marginBottom: 8 }}>{renderWithCitations(report.preBillDecision.rationale)}</p>
            {report.preBillDecision.recommendedNextStep && (
              <div style={{ fontSize: 12 }}><b>Next step:</b> {report.preBillDecision.recommendedNextStep}</div>
            )}
            {report.preBillDecision.secondaryNextStep && (
              <div style={{ fontSize: 12, marginTop: 4 }}><b>Then:</b> {report.preBillDecision.secondaryNextStep}</div>
            )}
          </div>
        </div>
      )}

      {/* Action Plan */}
      {(report.actionPlan || []).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Action Plan</h3>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
            {report.actionPlan.map((item, i) => <li key={i}>{item}</li>)}
          </ol>
        </div>
      )}

      {/* Audit Packet */}
      {report.auditPacket && ((report.auditPacket.recommended || []).length > 0 || (report.auditPacket.missingHelpful || []).length > 0) && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Audit Packet</h3>
          {(report.auditPacket.recommended || []).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Recommended Documents</div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                {report.auditPacket.recommended.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
          {(report.auditPacket.missingHelpful || []).length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Missing / Helpful Additional</div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#b07a16' }}>
                {report.auditPacket.missingHelpful.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Natural Language Analysis */}
      {report.naturalLanguageAnalysis && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', marginBottom: 10 }}>Analysis</h3>
          <div style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: '#2c3e50' }}>
            {renderWithCitations(report.naturalLanguageAnalysis)}
          </div>
        </div>
      )}

      {/* Sources & References */}
      {(report.sourceReferences || []).length > 0 && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--dm-muted)', cursor: 'pointer', marginBottom: 10 }}>
            Sources &amp; References ({report.sourceReferences.reduce((n, g) => n + (g.citations || []).length, 0)} citations from {report.sourceReferences.length} knowledge graph{report.sourceReferences.length > 1 ? 's' : ''})
          </summary>
          {report.sourceReferences.map((gr, i) => (
            <div key={i} style={{ marginBottom: 14, background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--dm-border)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                {gr.graphName}
                {gr.routingScore != null && <span style={{ fontSize: 11, marginLeft: 8, color: 'var(--dm-muted)' }}>(relevance: {(gr.routingScore * 100).toFixed(0)}%)</span>}
              </div>
              {(gr.citations || []).length > 0 && (
                <table className="suite-table" style={{ fontSize: 12 }}>
                  <thead><tr><th style={{ width: 30 }}>#</th><th>Source Document</th><th>Pages</th><th>Reference Text</th></tr></thead>
                  <tbody>
                    {gr.citations.map((c, ci) => (
                      <tr key={ci}>
                        <td style={{ color: 'var(--dm-muted)' }}>[{c.citeNum}]</td>
                        <td style={{ fontWeight: 600 }}>{c.sourceDoc || c.label || '—'}</td>
                        <td>{(c.pages || []).length > 0 ? `pp. ${c.pages.join(', ')}` : '—'}</td>
                        <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.referenceText || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(gr.citations || []).length === 0 && (gr.topOutcomeNodes || []).length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--dm-muted)' }}>
                  Top nodes: {gr.topOutcomeNodes.slice(0, 5).join(', ')}
                </div>
              )}
            </div>
          ))}
        </details>
      )}
    </section>
  );
}
